/**
 * Geographic grid keys for location-partitioned features (e.g. GeoChat rooms).
 *
 * Mirrors the backend traffic-service partition scheme
 * (backend/traffic-service/pipeline/producer.go: `grid_%d_%d_%d` = Z_X_Y from
 * a Web Mercator / slippy-tile cell), so a key produced here lines up with the
 * server's notion of a grid cell.
 */

// Neighborhood-sized cell. z=12 is a ~few-km tile — coarse enough that people in
// the same area share a room, fine enough that distant cities don't. Kept fixed
// (independent of the user's map zoom) so two users looking at the same place
// land in the same room regardless of how far each has zoomed in/out.
export const GEO_CHAT_GRID_ZOOM = 12;

// Latitude limit of the Web Mercator projection. Beyond this tan/sec blow up.
const MAX_MERCATOR_LAT = 85.05112878;

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

// Normalize longitude into [-180, 180). Maps an exact +180 to -180 so it stays
// in range (otherwise x would equal n, one past the last valid column).
const normalizeLng = (lng: number) => ((((lng + 180) % 360) + 360) % 360) - 180;

/**
 * Returns the slippy-tile cell (x, y) for a coordinate at the given zoom,
 * matching backend GetGridCell. Inputs are clamped/normalized to valid Web
 * Mercator ranges, and x/y are clamped to [0, n-1] so the cell is always valid.
 */
export function tileCell(
  lat: number,
  lng: number,
  zoom: number
): { x: number; y: number; z: number } {
  const n = 2 ** zoom;
  const safeLat = clamp(lat, -MAX_MERCATOR_LAT, MAX_MERCATOR_LAT);
  const latRad = (safeLat * Math.PI) / 180;
  const x = clamp(
    Math.floor(((normalizeLng(lng) + 180) / 360) * n),
    0,
    n - 1
  );
  const y = clamp(
    Math.floor(
      ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
    ),
    0,
    n - 1
  );
  return { x, y, z: zoom };
}

/**
 * Returns the grid room key for a location, e.g. "grid_12_2941_1847", or null
 * for non-finite coordinates (e.g. an uninitialized map center) — callers
 * should skip connecting until a valid key is available.
 */
export function gridKeyForLocation(
  lat: number,
  lng: number,
  zoom: number = GEO_CHAT_GRID_ZOOM
): string | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  const { x, y, z } = tileCell(lat, lng, zoom);
  return `grid_${z}_${x}_${y}`;
}
